import { paletteColor, paletteLabel } from './palette'
import { defaultRadiusM } from './toolUi'
import type { MapFeatureTap } from './ScenarioMapHost'
import type { AttachedAsset, ScenarioObject } from './types'

/** Map asset kind tint — palette colors only (hazard orange / infra road). */
export function mapAssetKindColor(kind: 'pipeline' | 'road'): string {
  return kind === 'pipeline' ? paletteColor('gas_leak') : paletteColor('road')
}
export function defaultParams(typeId: string): Record<string, unknown> {
  if (typeId === 'truck') return { cycleBlockedMs: 12_000, cycleClearMs: 12_000, blocked: true }
  if (typeId === 'road') return { status: 'open' }
  if (typeId === 'label') return { text: 'Label', color: '#ffffff' }
  if (typeId === 'sketch') return { color: '#E24B4A', width: 4, opacity: 0.9 }
  if (typeId === 'wind') {
    return {
      fromDeg: 280,
      speedMph: 10,
      plumeDistanceM: 420,
      plumeSpreadDeg: 26,
    }
  }
  if (typeId === 'tornado') {
    return {
      fromDeg: 250,
      speedMph: 70,
      radiusM: defaultRadiusM('tornado'),
    }
  }
  if (typeId === 'hurricane') {
    return {
      fromDeg: 90,
      speedMph: 40,
      radiusM: defaultRadiusM('hurricane'),
    }
  }
  const radiusM = defaultRadiusM(typeId)
  const base: Record<string, unknown> = radiusM != null ? { radiusM } : {}
  return base
}

/** Soft caps until board load is tested — board-wide and per place-only user. */
export const MAX_BOARD_OBJECTS = 120
export const MAX_OWNED_OBJECTS = 40
export function assetKey(asset: { kind: string; name: string; properties: Record<string, unknown> }): string {
  const id =
    asset.properties.BUXUS_ID ??
    asset.properties.SYS_NAME ??
    asset.properties.name ??
    asset.name
  return `${asset.kind}:${String(id)}`
}

function haversinePointM(aLng: number, aLat: number, bLng: number, bLat: number): number {
  const R = 6371000
  const toR = (d: number) => (d * Math.PI) / 180
  const dLat = toR(bLat - aLat)
  const dLng = toR(bLng - aLng)
  const lat1 = toR(aLat)
  const lat2 = toR(bLat)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

export function assetDistanceM(
  pose: { lng: number; lat: number },
  asset: { path?: [number, number][]; segments?: [number, number][][] },
): number | null {
  const segs =
    asset.segments && asset.segments.length
      ? asset.segments
      : asset.path && asset.path.length >= 2
        ? [asset.path]
        : []
  if (!segs.length) return null
  let best = Number.POSITIVE_INFINITY
  for (const seg of segs) {
    for (const pt of seg) {
      best = Math.min(best, haversinePointM(pose.lng, pose.lat, pt[0], pt[1]))
    }
  }
  return Number.isFinite(best) ? best : null
}

export function assetToHighlightTap(
  asset: AttachedAsset,
  pose: { lng: number; lat: number } | null,
): MapFeatureTap {
  const path = asset.path ?? asset.segments?.[0] ?? []
  const segments = asset.segments?.length ? asset.segments : path.length >= 2 ? [path] : []
  return {
    kind: asset.kind,
    name: asset.name,
    properties: asset.properties,
    path,
    segments,
    lng: pose?.lng ?? path[0]?.[0] ?? 0,
    lat: pose?.lat ?? path[0]?.[1] ?? 0,
    distanceM: pose ? assetDistanceM(pose, asset) ?? undefined : undefined,
  }
}

export type NearbySortCol = 'checked' | 'type' | 'name' | 'dist'
export type LinkedSortCol = 'type' | 'name' | 'dist'

export function readAttachedAssets(params: Record<string, unknown>): AttachedAsset[] {
  const many = params.attachedAssets
  if (Array.isArray(many)) {
    return many.filter(
      (a): a is AttachedAsset =>
        a != null &&
        typeof a === 'object' &&
        (a.kind === 'pipeline' || a.kind === 'road') &&
        typeof a.name === 'string',
    )
  }
  const one = params.attachedAsset
  if (one && typeof one === 'object' && 'kind' in one && 'name' in one) {
    const a = one as AttachedAsset
    if (a.kind === 'pipeline' || a.kind === 'road') return [a]
  }
  return []
}

export function writeAttachedAssets(params: Record<string, unknown>, assets: AttachedAsset[]): Record<string, unknown> {
  const next = { ...params }
  delete next.attachedAsset
  if (assets.length) next.attachedAssets = assets
  else delete next.attachedAssets
  return next
}

/** Merge path/segments onto a road object from its attached road assets. */
export function withRoadGeometryFromAssets(obj: ScenarioObject, assets: AttachedAsset[]): ScenarioObject {
  if (obj.type !== 'road') {
    return { ...obj, params: writeAttachedAssets(obj.params, assets) }
  }
  const roadAssets = assets.filter((a) => a.kind === 'road')
  const segs: [number, number][][] = []
  for (const a of roadAssets) {
    if (a.segments?.length) {
      for (const seg of a.segments) {
        if (seg.length >= 2) segs.push(seg)
      }
    } else if (a.path && a.path.length >= 2) {
      segs.push(a.path)
    }
  }
  const params = writeAttachedAssets(obj.params, assets)
  if (!segs.length) {
    const next = { ...obj, params }
    delete next.path
    delete params.segments
    return next
  }
  const longest = [...segs].sort((a, b) => b.length - a.length)[0]!
  const primary = roadAssets[0]
  const mid = longest[Math.floor(longest.length / 2)]!
  return {
    ...obj,
    path: longest,
    pose: { lng: mid[0], lat: mid[1] },
    label:
      obj.label === paletteLabel('road') && primary?.name
        ? primary.name
        : obj.label,
    params: { ...params, segments: segs },
  }
}

export function attachKindsForObject(typeId: string): Array<'pipeline' | 'road'> {
  if (typeId === 'road') return ['road']
  return ['pipeline', 'road']
}
